/**
 * Good Atoms Constellation Knowledge Map
 *
 * Replaces the linear pathway progress nodes with an animated night-sky star map.
 *   • Each lesson = a star at an organic position on the sky canvas
 *   • Completed stars glow with the pathway's subject colour
 *   • The "next" star pulses with an expanding ring
 *   • Locked stars are dim and non-interactive
 *   • Constellation lines connect adjacent stars — light up as lessons are completed
 *   • Background field of micro-stars twinkles independently for depth
 *   • When all lessons are complete the constellation name glows at the bottom
 */

import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { SAMPLE_LESSONS } from '../../data/pathways'
import type { Pathway, Progress } from '../../types'
import { useT } from '../../i18n'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConstellationMapProps {
  pathway:      Pathway
  progress:     Progress | null
  nextLessonId: string | undefined
}

// ─── Static background star field ────────────────────────────────────────────
// Fixed coordinates so React never remounts them. [x, y, radius, twinkle-phase]

const BG_STARS: [number, number, number, number][] = [
  [18,14,0.8,0],[45,8,1.1,1],[78,22,0.6,2],[115,12,0.9,0],[148,30,0.7,3],
  [180,8,1.1,1],[220,18,0.8,2],[258,28,0.7,0],[295,10,1.2,3],[330,22,0.8,1],
  [365,14,1.0,2],[390,35,0.6,0],[8,55,0.9,3],[35,70,0.6,1],[72,48,1.1,2],
  [105,62,0.7,0],[145,44,1.2,3],[175,68,0.6,1],[215,52,0.9,2],[248,72,0.7,0],
  [280,45,1.0,3],[318,58,0.8,1],[355,68,0.6,2],[390,50,1.0,0],[12,100,0.7,3],
  [52,110,1.0,1],[88,88,0.8,2],[128,102,0.6,0],[165,85,1.2,3],[198,108,0.9,1],
  [235,92,0.7,2],[268,110,1.1,0],[305,85,0.8,3],[338,100,0.6,1],[374,90,1.0,2],
  [22,148,0.9,0],[58,162,0.7,3],[95,142,1.1,1],[132,168,0.8,2],[158,145,0.6,0],
  [195,162,1.0,3],[225,145,0.8,1],[262,168,0.7,2],[298,148,1.2,0],[335,162,0.9,3],
  [368,148,0.7,1],[5,190,0.8,2],[42,200,0.6,0],[85,188,1.0,3],[118,205,0.7,1],
  [155,198,0.9,2],[192,185,1.1,0],[228,205,0.7,3],[265,192,0.8,1],[302,200,0.6,2],
  [340,188,1.0,0],[375,200,0.8,3],[396,175,0.7,1],
]

// ─── Star positions per constellation size ────────────────────────────────────
// Organic patterns — each feels like a real mini-constellation

const CONSTELLATION_POSITIONS: Record<number, [number, number][]> = {
  1: [[200, 110]],
  2: [[130, 110], [270, 110]],
  3: [[80, 155], [200, 55], [320, 155]],
  4: [[70, 155], [155, 72], [248, 82], [330, 158]],
  5: [[62, 162], [142, 76], [200, 130], [278, 62], [338, 155]],
  6: [[55, 162], [120, 85], [185, 148], [238, 70], [302, 118], [358, 162]],
  7: [[50, 162], [112, 90], [172, 148], [218, 72], [272, 128], [332, 78], [378, 158]],
}

function getPositions(count: number): [number, number][] {
  return CONSTELLATION_POSITIONS[Math.min(Math.max(count, 1), 7)]
    ?? CONSTELLATION_POSITIONS[5]
}

// ─── Subject colours ──────────────────────────────────────────────────────────

const STAR_COLORS: Record<string, string> = {
  mathematics:  '#F59E0B',
  science:      '#34D399',
  technology:   '#60A5FA',
  engineering:  '#F87171',
  arts:         '#C084FC',
  nature:       '#4ADE80',
  futureskills: '#A78BFA',
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ConstellationMap({ pathway, progress, nextLessonId }: ConstellationMapProps) {
  const navigate = useNavigate()
  const { t, lang }   = useT()
  const completedIds  = progress?.lessonsCompleted ?? []
  const allComplete   = completedIds.length >= pathway.totalLessons
  const starColor     = STAR_COLORS[pathway.subject] ?? '#F59E0B'
  const positions     = getPositions(pathway.lessonIds.length)

  return (
    <div className="glass-card rounded-3xl overflow-hidden">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 pt-6 pb-3">
        <div>
          <p className="text-ink/50 text-xs font-extrabold uppercase tracking-widest">
            {t('path.label')}
          </p>
          <p className="text-ink font-extrabold text-base mt-0.5">
            {lang === 'en' ? (pathway.titleEn ?? pathway.title) : pathway.title}
          </p>
        </div>
        <span className="text-sm font-extrabold text-ink/50 bg-ink/5 px-3 py-1.5 rounded-full border border-ink/8">
          {completedIds.length}/{pathway.totalLessons}
        </span>
      </div>

      {/* ── Night-sky canvas ──────────────────────────────────────────────── */}
      <div
        className="mx-4 mb-4 rounded-2xl overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #0B1225 0%, #131B38 55%, #0E1628 100%)' }}
      >
        <svg viewBox="0 0 400 230" width="100%" style={{ display: 'block' }} role="img" aria-label={lang === 'en' ? `${pathway.titleEn ?? pathway.title} constellation map` : `${pathway.title} stjernekartet`}>

          {/* SVG filter defs */}
          <defs>
            <filter id="star-glow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="star-glow-strong" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="sky-bg" cx="50%" cy="40%" r="65%">
              <stop offset="0%"   stopColor="#1A2545" />
              <stop offset="100%" stopColor="#070C1A" />
            </radialGradient>
          </defs>

          {/* Sky fill */}
          <rect width="400" height="230" fill="url(#sky-bg)" />

          {/* ── Background micro-stars (twinkle independently) ───────────── */}
          <g aria-hidden="true">
          {BG_STARS.map(([x, y, r, phase], i) => (
            <motion.circle
              key={i}
              cx={x} cy={y} r={r}
              fill="white"
              animate={{ opacity: [0.15, 0.55, 0.15] }}
              transition={{
                duration: 2.5 + (phase * 0.8),
                repeat: Infinity,
                delay: (i * 0.17) % 3.5,
                ease: 'easeInOut',
              }}
            />
          ))}
          </g>

          {/* ── Constellation lines ───────────────────────────────────────── */}
          {pathway.lessonIds.slice(0, -1).map((lessonId, idx) => {
            const [x1, y1] = positions[idx]     ?? [0, 0]
            const [x2, y2] = positions[idx + 1] ?? [0, 0]
            const bothDone =
              completedIds.includes(lessonId) &&
              completedIds.includes(pathway.lessonIds[idx + 1])

            return (
              <motion.line
                key={idx}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={bothDone ? starColor : 'rgba(255,255,255,0.18)'}
                strokeWidth={bothDone ? 1.5 : 1}
                strokeDasharray="5 6"
                initial={{ opacity: 0 }}
                animate={{ opacity: bothDone ? 0.65 : 0.3 }}
                transition={{ duration: 0.9, delay: 0.3 + idx * 0.08 }}
              />
            )
          })}

          {/* ── Lesson stars ─────────────────────────────────────────────── */}
          {pathway.lessonIds.map((lessonId, idx) => {
            const lesson   = SAMPLE_LESSONS[lessonId]
            const isDone   = completedIds.includes(lessonId)
            const isNext   = lessonId === nextLessonId
            const isLocked = !isDone && !isNext
            const pos      = positions[idx] ?? [200, 110]
            const [cx, cy] = pos
            const r        = isDone ? 9 : isNext ? 11 : 7
            const canClick = !!lesson && !isLocked

            const lessonTitle = lesson
              ? (lang === 'en' && lesson.titleEn ? lesson.titleEn : lesson.title)
              : `Lesson ${idx + 1}`;
            const starLabel = isDone
              ? `${lessonTitle} — ${lang === 'en' ? 'completed' : 'fullført'}`
              : isNext
              ? `${lessonTitle} — ${lang === 'en' ? 'start lesson' : 'start leksjon'}`
              : `${lessonTitle} — ${lang === 'en' ? 'locked' : 'låst'}`;

            return (
              <g
                key={lessonId}
                role={canClick ? 'button' : undefined}
                tabIndex={canClick ? 0 : undefined}
                aria-label={starLabel}
                style={{ cursor: canClick ? 'pointer' : 'default' }}
                onClick={() => canClick && navigate(`/lesson/${lessonId}`)}
                onKeyDown={(e) => { if (canClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); navigate(`/lesson/${lessonId}`); } }}
              >
                {/* Pulsing ring for "next" star */}
                {isNext && (
                  <motion.circle
                    cx={cx} cy={cy} r={24}
                    fill="none"
                    stroke={starColor}
                    strokeWidth="1.5"
                    animate={{ r: [18, 32], opacity: [0.6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                  />
                )}

                {/* Glow halo for completed stars */}
                {isDone && (
                  <circle
                    cx={cx} cy={cy} r={r + 5}
                    fill={starColor}
                    opacity="0.18"
                    filter="url(#star-glow-strong)"
                  />
                )}

                {/* Star body */}
                <motion.circle
                  cx={cx} cy={cy} r={r}
                  fill={
                    isDone   ? starColor :
                    isNext   ? `${starColor}BB` :
                    'rgba(255,255,255,0.1)'
                  }
                  stroke={isDone || isNext ? starColor : 'rgba(255,255,255,0.3)'}
                  strokeWidth={isDone ? 0 : 1.5}
                  filter={isDone || isNext ? 'url(#star-glow)' : undefined}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale:   isDone ? [1, 1.12, 1] : 1,
                    opacity: isLocked ? 0.3 : 1,
                  }}
                  transition={isDone
                    ? { duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: idx * 0.25 }
                    : { duration: 0.45, delay: 0.25 + idx * 0.09, ease: 'backOut' }
                  }
                />

                {/* Label inside star */}
                <motion.text
                  x={cx} y={cy + 0.5}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={isDone ? '7.5' : isNext ? '8.5' : '8'}
                  fill={isDone ? 'white' : isNext ? 'white' : 'rgba(255,255,255,0.35)'}
                  fontWeight="bold"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 + idx * 0.09 }}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {isDone ? '✓' : idx + 1}
                </motion.text>

                {/* Lesson title below star */}
                {lesson && (
                  <motion.text
                    x={cx}
                    y={cy + r + 15}
                    textAnchor="middle"
                    fontSize="7.5"
                    fill={
                      isDone   ? 'rgba(255,255,255,0.70)' :
                      isNext   ? 'rgba(255,255,255,0.55)' :
                      'rgba(255,255,255,0.22)'
                    }
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.55 + idx * 0.09 }}
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {(lang === 'en' && lesson.titleEn ? lesson.titleEn : lesson.title).slice(0, 15)}
                  </motion.text>
                )}
              </g>
            )
          })}

          {/* ── Constellation complete banner ─────────────────────────────── */}
          <AnimatePresence>
            {allComplete && (
              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                {/* Subtle glow line */}
                <motion.text
                  x="200" y="218"
                  textAnchor="middle"
                  fontSize="9.5"
                  fontWeight="bold"
                  fill={starColor}
                  filter="url(#star-glow)"
                  style={{ userSelect: 'none' }}
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                >
                  ✦ {lang === 'en' ? (pathway.titleEn ?? pathway.title) : pathway.title} ✦
                </motion.text>
              </motion.g>
            )}
          </AnimatePresence>

        </svg>
      </div>
    </div>
  )
}
